import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const { t } = useTranslation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Note: This would need to be implemented in the backend
      // For now, we'll simulate the API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-primary via-brand-secondary to-brand-accent flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/20" />

      <div className="relative bg-white rounded-3xl shadow-soft p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="mx-auto h-16 w-16 bg-brand-primary/10 rounded-full flex items-center justify-center mb-4">
            <span className="text-2xl">🔑</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-800 mb-2">
            {t('auth.forgotPassword.title', 'Forgot Password?')}
          </h1>
          <p className="text-sm text-slate-600">
            {t('auth.forgotPassword.subtitle', 'Enter your email address and we\'ll send you a link to reset your password')}
          </p>
        </div>

        {success ? (
          <div className="text-center space-y-4">
            <div className="h-16 w-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
              <span className="text-2xl">✉️</span>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">
                Check Your Email
              </h3>
              <p className="text-sm text-slate-600">
                We've sent a password reset link to <strong>{email}</strong>.
                Please check your inbox and follow the instructions.
              </p>
            </div>
            <div className="pt-4 space-y-3">
              <p className="text-xs text-slate-500">
                Didn't receive the email? Check your spam folder or
                <button
                  onClick={() => setSuccess(false)}
                  className="text-brand-primary hover:underline ml-1"
                >
                  try again
                </button>
              </p>
              <Link
                to="/login"
                className="inline-flex items-center justify-center w-full rounded-full bg-brand-primary px-6 py-3 text-sm font-semibold text-white shadow-soft transition hover:bg-brand-secondary"
              >
                Back to Login
              </Link>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-600">
                <span className="text-lg leading-none">⚠️</span>
                <div>
                  <p className="font-semibold">Error</p>
                  <p>{error}</p>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                {t('auth.forgotPassword.emailLabel', 'Email Address')}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError(null);
                }}
                placeholder={t('auth.forgotPassword.emailPlaceholder', 'Enter your email')}
                disabled={loading}
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20 disabled:opacity-50"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-primary px-6 py-3 text-sm font-semibold text-white shadow-soft transition hover:bg-brand-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent disabled:cursor-not-allowed disabled:bg-brand-primary/60"
            >
              {loading ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                  <span>{t('auth.forgotPassword.sending', 'Sending...')}</span>
                </>
              ) : (
                <>
                  <span>📧</span>
                  <span>{t('auth.forgotPassword.submit', 'Send Reset Link')}</span>
                </>
              )}
            </button>

            <div className="text-center space-y-4">
              <Link
                to="/login"
                className="text-sm text-brand-primary hover:text-brand-secondary font-medium"
              >
                ← {t('auth.forgotPassword.backToLogin', 'Back to Login')}
              </Link>

              <div className="text-xs text-slate-500">
                <p>
                  {t('auth.forgotPassword.rememberPassword', 'Remember your password?')}{' '}
                  <Link to="/login" className="text-brand-primary hover:underline">
                    {t('auth.forgotPassword.signIn', 'Sign in')}
                  </Link>
                </p>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;